import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  GetTicketContextDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { Command } from "~/components/guides/components";
import {
  helperTextStyles,
  isValidHttpsUrl,
  ValidationMessage,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { IntegrationToggleOption } from "~/pages/integrations/components/toggle-option/IntegrationToggleOption";

export type WebhookFormInput = {
  integrationName: string;
  webhookUrl: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
};

const defaultValues: WebhookFormInput = {
  integrationName: "",
  webhookUrl: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
};

export function WebhookIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      GetTicketContextDocument,
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "WebhookConfigurationOptions") return;

      reset({
        integrationName: updateFlow.integration.name,
        webhookUrl: configOptions.url || "",
        autoCloseTickets: configOptions.autoCloseTickets || false,
        autoCreateTickets: configOptions.autoCreateTickets || false,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<WebhookFormInput> = async (data) => {
    const webhookConfigurationOptions = {
      url: data.webhookUrl.trim(),
      autoCreateTickets: data.autoCreateTickets,
      autoCloseTickets: data.autoCloseTickets,
    };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemWebhook,
              configurationOptions: {
                webhookConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });

        navigate(
          `/space/integrations/ticket_system_webhook/${integrationId}/?${scope.params}`,
        );
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemWebhook,
              longLivedToken: false,
              configurationOptions: {
                webhookConfigurationOptions,
              },
            },
          },
        });
      }
      if (redirectToParam) {
        navigate(redirectToParam);
      } else {
        navigate(`/space/integrations/ticket_system_webhook?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["Webhook", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader
        {...{ type: IntegrationType.TicketSystemWebhook }}
      />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <>
            {/* Step 1 */}
            <Box pb={4}>
              <Command
                number={1}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Name your ticket integration
              </Command>

              <Box pl={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: 2 }}
                >
                  This name will appear wherever you create tickets from
                  findings.
                </Typography>
                <Controller
                  name="integrationName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      sx={{
                        background: (theme) => theme.palette.code.background,
                        borderRadius: 1,
                        color: "text.primary",
                        ...helperTextStyles,
                      }}
                      placeholder="Your integration name..."
                      error={Boolean(errors.integrationName)}
                      helperText={
                        Boolean(errors.integrationName) && (
                          <ValidationMessage error={errors.integrationName} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Step 2 */}
            <Box pb={4}>
              <Command
                number={2}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Add your webhook URL
              </Command>

              <Box pl={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: 2 }}
                >
                  Send JSON-encoded data to this HTTP endpoint when a new ticket
                  is created.
                </Typography>
                <Controller
                  name="webhookUrl"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      isHttpsUrl: (value) => !value || isValidHttpsUrl(value),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      sx={{
                        background: theme.palette.code.background,
                        borderRadius: 1,
                        color: "text.primary",
                        ...helperTextStyles,
                      }}
                      placeholder="Your webhook URL..."
                      error={Boolean(errors.webhookUrl)}
                      helperText={
                        Boolean(errors.webhookUrl) && (
                          <ValidationMessage error={errors.webhookUrl} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Step 3 */}
            <Box pb={4}>
              <Command
                number={3}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Configure preferences
              </Command>
              <Box mt={3} pl={6}>
                <Box mb={2}>
                  <IntegrationToggleOption
                    title="Automatically close issues"
                    description="If you create a ticket in Mondoo and then resolve it, the corresponding webhook integration issue closes."
                    formOptions={{
                      name: "autoCloseTickets",
                      control,
                    }}
                  />
                </Box>
                <IntegrationToggleOption
                  title="Automatically create tickets on drift"
                  description="If 'Automatically create tickets on drift' is enabled in space settings, create an issue in this integration for each new drift ticket. "
                  formOptions={{
                    name: "autoCreateTickets",
                    control,
                  }}
                />
              </Box>
            </Box>

            <Box width={1}>
              <Divider />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "end", mt: 5 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                endIcon={<ChevronRightIcon />}
                // setValue doesn't trigger the required validation so we're doing it manually here
                disabled={!isValid}
              >
                {updateFlow ? "update configuration" : "create integration"}
              </Button>
            </Box>
          </>
        </form>
      </Box>
    </Fragment>
  );
}
